import { useCallback } from 'react';
import type { WorkboardView } from 'dashboard409a/components/NewViewDialog/types';
import { useLocalStorage } from 'services/hooks/useLocalStorage';

export const useWorkboardViews = (sectionId?: string) => {
  const key = `workboard_views_${sectionId ?? 'global'}`;
  const [views, setViews] = useLocalStorage<WorkboardView[]>(key, []);

  const addView = useCallback(
    (view: WorkboardView) => {
      setViews(prev => [...(prev ?? []), view]);
    },
    [setViews]
  );

  const removeView = useCallback(
    (id: string) => {
      setViews(prev => (prev ?? []).filter(v => v.id !== id));
    },
    [setViews]
  );

  const updateView = useCallback(
    (updated: WorkboardView) => {
      setViews(prev => (prev ?? []).map(v => (v.id === updated.id ? updated : v)));
    },
    [setViews]
  );

  const cloneView = useCallback(
    (id: string) => {
      setViews(prev => {
        const existing = prev ?? [];
        const source = existing.find(v => v.id === id);
        if (!source) return existing;
        const existingNames = new Set(existing.map(v => v.name));
        let cloneName = `${source.name} (Copy)`;
        let i = 2;
        while (existingNames.has(cloneName)) {
          cloneName = `${source.name} (Copy ${i++})`;
        }
        return [...existing, { ...source, id: crypto.randomUUID(), name: cloneName }];
      });
    },
    [setViews]
  );

  return { views: views ?? [], addView, removeView, updateView, cloneView };
};
